import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PasswordInput } from "@/components/ui/password-input";
import { Sparkles, ArrowLeft, Globe } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "", gender: "female", referral_code: (params.get("ref") || "").toUpperCase() });
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    email_login_enabled: true,
    phone_login_enabled: false,
    google_login_enabled: false,
    google_client_id: ""
  });
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => setSettings(data))
      .catch(() => {});
  }, []);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      // Clean and validate phone number
      const cleanPh = form.phone.trim().replace(/\s+/g, "").replace("+91", "");
      if (!/^\d{10}$/.test(cleanPh)) {
        throw new Error(language === "hi" ? "कृपया एक वैध 10-अंकीय मोबाइल नंबर दर्ज करें।" : "Please enter a valid 10-digit mobile number.");
      }

      // Store local referral code if entered
      if (form.referral_code) {
        localStorage.setItem("jj_referral_code", form.referral_code.toUpperCase().trim());
      }
      
      const payload = {
        email: form.email,
        password: form.password,
        name: form.name,
        phone: cleanPh,
        gender: form.gender,
        referral_code: form.referral_code
      };
      
      await register(payload);
      navigate("/verify-otp", { state: { email: form.email } });
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1 relative">
        <div className="absolute inset-0 mandala-bg pointer-events-none" />
        <div className="relative w-full max-w-md">
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary" data-testid="reg-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1"/>{language === "hi" ? "पीछे" : "Back"}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLanguage(language === "en" ? "hi" : "en")}
              className="rounded-full flex items-center gap-1.5 px-3 hover:bg-muted"
              data-testid="lang-switch-btn"
            >
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase">{language === "en" ? "हिं" : "EN"}</span>
            </Button>
          </div>
          <h1 className="font-heading text-4xl text-primary">{t("auth.registerTitle")}</h1>
          <p className="text-muted-foreground mt-2">{t("auth.registerSubtitle")}</p>

          {settings.email_login_enabled ? (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label>{language === "hi" ? "यह प्रोफाइल किसके लिए है?" : "This profile is for"}</Label>
                <RadioGroup value={form.gender} onValueChange={(v) => update("gender", v)} className="grid grid-cols-2 gap-3 mt-2">
                  {["female", "male"].map((g) => (
                    <div 
                      key={g} 
                      onClick={() => update("gender", g)}
                      className={`cursor-pointer flex items-center gap-3 rounded-xl border p-3 ${form.gender === g ? "border-primary bg-accent" : "border-border hover:bg-slate-50"}`}
                    >
                      <RadioGroupItem value={g} id={`gender-${g}`} data-testid={`reg-gender-${g}`} onClick={(e) => e.stopPropagation()} />
                      <Label htmlFor={`gender-${g}`} className="capitalize text-sm cursor-pointer select-none">
                        {g === "female" ? (language === "hi" ? "दुल्हन / महिला" : "Bride / Woman") : (language === "hi" ? "दूल्हा / पुरुष" : "Groom / Man")}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label htmlFor="r-name">{t("auth.fullName")}</Label>
                <Input id="r-name" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="reg-name" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="r-phone">{language === "hi" ? "मोबाइल नंबर (10 अंक)" : "Mobile Number (10 digits)"}</Label>
                <Input id="r-phone" type="tel" required value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="e.g. 9876543210" data-testid="reg-phone" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="r-email">{t("auth.email")}</Label>
                <Input id="r-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="reg-email" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="r-password">{t("auth.password")}</Label>
                <div className="mt-1.5">
                  <PasswordInput 
                    id="r-password" 
                    required 
                    minLength={6} 
                    value={form.password} 
                    onChange={(e) => update("password", e.target.value)} 
                    data-testid="reg-password" 
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === "hi" ? "कम से कम 6 अक्षर।" : "At least 6 characters."}
                </p>
              </div>
              <div>
                <Label htmlFor="r-referral">{language === "hi" ? "रेफरल कोड (वैकल्पिक)" : "Referral code (optional)"}</Label>
                <Input id="r-referral" value={form.referral_code} onChange={(e) => update("referral_code", e.target.value.toUpperCase())} placeholder={language === "hi" ? "मित्र का कोड है?" : "Have a friend's code?"} data-testid="reg-referral" className="mt-1.5 font-mono tracking-widest" />
              </div>
              {err && <p className="text-sm text-destructive" data-testid="reg-error">{err}</p>}
              <Button type="submit" disabled={loading} className="w-full rounded-full h-11 btn-press" data-testid="reg-submit-btn">
                {loading ? (language === "hi" ? "बन रहा है..." : "Creating...") : t("auth.btnRegister")}
              </Button>
            </form>
          ) : (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl text-sm space-y-1">
              <p className="font-semibold">{language === "hi" ? "नए सदस्यों के लिए ईमेल पंजीकरण अक्षम है।" : "Email registration is disabled for new members."}</p>
              <p className="text-amber-800/90 text-xs">
                {language === "hi" ? "कृपया नीचे दिए गए अपने फोन नंबर या गूगल खाते का उपयोग करके पंजीकरण करें।" : "Please register or sign up using your phone number or Google account below."}
              </p>
            </div>
          )}

          {(settings.phone_login_enabled || settings.google_login_enabled) && (
            <>
              <div className="relative my-5">
                <div className="ornament-divider" />
              </div>

              <div className="space-y-3">
                {settings.phone_login_enabled && (
                  <Button asChild variant="outline" className="w-full rounded-full h-11 justify-center" data-testid="reg-phone-btn">
                    <Link to="/login/phone">{t("auth.phoneLoginBtn")}</Link>
                  </Button>
                )}
                {settings.google_login_enabled && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full rounded-full h-11 flex items-center justify-center gap-2"
                    onClick={() => {
                      const googleClientId = settings.google_client_id || "1024963400258-ka7ghj9i9hcdobbtrlmhsd87r0biqf71.apps.googleusercontent.com";
                      const redirectUri = encodeURIComponent("https://hamarsathi.com/user/social-login/callback/google");
                      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid%20email%20profile`;
                    }}
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#EA4335" d="M12 5.04c1.7 0 3.2.6 4.4 1.8l3.3-3.3C17.7 1.6 15 1 12 1 7.2 1 3.2 3.8 1.4 8l3.9 3c1-3 3.8-6 6.7-6z"/><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.6h6.5c-.3 1.5-1.1 2.8-2.4 3.7l3.7 2.9c2.2-2 3.7-5 3.7-9z"/><path fill="#FBBC05" d="M5.3 14.8c-.3-.8-.4-1.8-.4-2.8s.1-2 .4-2.8L1.4 6.2C.5 8 .0 10 .0 12c0 2 .5 4 1.4 5.8l3.9-3z"/><path fill="#34A853" d="M12 23c3.2 0 6-1.1 8-3l-3.7-2.9c-1.1.7-2.5 1.2-4.3 1.2-3.4 0-6.2-2.3-7.2-5.4l-3.9 3C3.2 20.2 7.2 23 12 23z"/></svg>
                    {language === "hi" ? "गूगल के साथ जारी रखें" : "Continue with Google"}
                  </Button>
                )}
              </div>
            </>
          )}

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {t("auth.alreadyRegistered")}{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="reg-to-login">
              {t("nav.login")}
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1722952934708-749c22eb2e58" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-bl from-primary/80 via-primary/40 to-transparent" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <Link to="/" className="inline-flex items-center gap-2 self-end"><Sparkles className="w-5 h-5 text-secondary"/>
            <span className="font-heading text-2xl">Hamarsathi</span>
          </Link>
          <div>
            <h2 className="font-heading text-5xl leading-tight">
              {language === "hi" ? <>एक नया अध्याय<br/>आज से शुरू हो रहा है।</> : <>A new chapter<br/>starts today.</>}
            </h2>
            <p className="mt-4 text-white/85 max-w-md">
              {language === "hi" ? "पूरे झारखंड के परिवारों के साथ जुड़ें और अपना हमसफर पाएं।" : "Join families across Jharkhand on their journey to forever."}
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-white/70 self-end">
            {language === "hi" ? "100% सत्यापित" : "100% Verified"}
          </div>
        </div>
      </div>
    </div>
  );
}
